import { COMMERCIAL_HUBS, formatGrokInboxAirportBrief } from "./airportDoctrine";
import { basesLookAtlantic } from "./baseGravity";
import { hydrateSceneryAirport, resolveCoords } from "./airports";
import { haversineNm } from "./geo";
import { CHARTER_RANGE_FACTOR } from "./hawker800xp";
import { anyInspectionOverdue, hoursToInspection, hasOpenGroundSquawk } from "./maintenance";
import { hasGroundingMel } from "./mel";
import {
  CORPORATE_DESTS,
  GOLF_MARKETS,
  MEDICAL_DESTS,
  SUN_DESTS,
  VIP_DESTS,
  seasonId,
  type SeasonId,
} from "./season";
import type { AircraftSpec, CompanyMxBase, FlightType, MelItem, SceneryAirport, Squawk } from "./types";

/**
 * Jet charter floor. A home-region hop (KTEB–KBOS, KTEB–KBED) is a real charter.
 * The old 200 NM / 8%-of-range floor skipped that whole region and left Canada
 * as the nearest legal dest. Named Atlantic shorts stay banned separately.
 */
export const JET_MIN_STAGE_NM = 120;
/** Turboprop / utility can take the Cape Breton shorts. */
export const TURBOPROP_MIN_STAGE_NM = 20;
/** Empty mx ferry may still be a short hop (CYPD→CYHZ). */
export const FERRY_MIN_STAGE_NM = 20;
/** Offer a paying mx-positioning RFQ when inspection is due or this close. */
export const MX_PAYLOAD_SOON_HOURS = 25;

/** ICAO types we treat as jets for stage-length (H25B = Hawker 800XP). */
export const JET_ICAO_TYPES = new Set(["H25B", "FA50"]);

function looksLikeBizjetType(icaoType?: string, name?: string): boolean {
  const t = (icaoType ?? "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (JET_ICAO_TYPES.has(t)) return true;
  if (t.startsWith("FA") || t.startsWith("C25") || t.startsWith("C56") || t.startsWith("CL3") || t.startsWith("GLF") || t.startsWith("E55")) {
    return true;
  }
  return /\b(FALCON|HAWKER|CITATION|GULFSTREAM|LEAR|CHALLENGER|PHENOM)\b/i.test(name ?? "");
}

/**
 * Explicit Atlantic shorts the jet must not bid — even if scenery coords are off.
 * CYPD→CYHZ 102 NM, CYPD→CYQM 141 NM; same idea for nearby hops.
 */
export const SHORT_REGIONAL_HOPS: ReadonlyArray<readonly [string, string]> = [
  ["CYPD", "CYHZ"],
  ["CYPD", "CYQM"],
  ["CYPD", "CYQY"],
  ["CYPD", "CYYG"],
  ["CYHZ", "CYQM"],
  ["CYHZ", "CYYG"],
  ["CYHZ", "CYQY"],
  ["CYQY", "CYYG"],
];

/** Cabot / Cape Breton golf country — no winter golf RFQs here. */
export const ATLANTIC_GOLF_ICAOS = ["CYPD", "CYQY", "CYYG", "CYHZ"] as const;

export const SKI_DESTS = ["CYHU", "CYQB", "KBTV", "KMHT", "KBED", "CYOW", "CYUL", "KBOS"] as const;
export const HUNTING_DESTS = ["CYYT", "CYQX", "CYYR", "KBGR", "CYQM", "CYDF"] as const;
export const FISHING_DESTS = ["CYYT", "CYQX", "CYYR", "CYQY", "KBGR", "CYGP"] as const;
export const FAMILY_DESTS = ["CYHZ", "CYYT", "CYHU", "CYHM", "KBED", "CYQM", "CYYG", "CYOW", "CYUL", "CYYZ"] as const;
export const TOURISM_DESTS = ["CYPD", "CYQY", "CYYG", "CYYT", "CYHZ", "KACK"] as const;
export const CONNECTION_DESTS = COMMERCIAL_HUBS;

export type SeasonalPurposeId =
  | "golf"
  | "sun"
  | "corporate"
  | "vip"
  | "medical"
  | "family"
  | "hunting"
  | "fishing"
  | "ski"
  | "tourism"
  | "connection"
  | "mx_payload"
  | "ferry";

export const PURPOSE_TO_FLIGHT_TYPE: Record<SeasonalPurposeId, FlightType> = {
  golf: "golf",
  sun: "sun",
  corporate: "corporate",
  vip: "vip",
  medical: "medical",
  family: "vip",
  hunting: "vip",
  fishing: "vip",
  ski: "vip",
  tourism: "vip",
  connection: "corporate",
  mx_payload: "corporate",
  ferry: "ferry",
};

export type PurposeWeights = Record<SeasonalPurposeId, number>;

export function isJetSpec(spec: Pick<AircraftSpec, "icaoType" | "cruiseKt"> & { name?: string }): boolean {
  if (looksLikeBizjetType(spec.icaoType, spec.name)) return true;
  return spec.cruiseKt >= 360;
}

export function minStageNmFor(
  spec: Pick<AircraftSpec, "icaoType" | "cruiseKt" | "rangeNm">,
  ferry = false,
): number {
  if (ferry) return FERRY_MIN_STAGE_NM;
  if (!isJetSpec(spec)) return TURBOPROP_MIN_STAGE_NM;
  return JET_MIN_STAGE_NM;
}

export function pairKey(a: string, b: string): string {
  const x = a.trim().toUpperCase();
  const y = b.trim().toUpperCase();
  return x < y ? `${x}-${y}` : `${y}-${x}`;
}

export function isKnownShortRegionalHop(originIcao: string, destIcao: string): boolean {
  const key = pairKey(originIcao, destIcao);
  return SHORT_REGIONAL_HOPS.some(([a, b]) => pairKey(a, b) === key);
}

export function isAtlanticGolfRegion(icao: string): boolean {
  return (ATLANTIC_GOLF_ICAOS as readonly string[]).includes(icao.trim().toUpperCase());
}

export function isGolfSeasonMonth(month: number): boolean {
  return seasonId(month) === "golf";
}

/** Golf inbound to Cabot / nearby only in May–Oct. Winter golf belongs in the south (or not at all). */
export function isGolfAllowed(originIcao: string, destIcao: string, month: number): boolean {
  if (isGolfSeasonMonth(month)) return true;
  if (isAtlanticGolfRegion(originIcao) || isAtlanticGolfRegion(destIcao)) return false;
  return true;
}

export function remapUnseasonalType(
  type: FlightType,
  originIcao: string,
  destIcao: string,
  month: number,
): FlightType {
  if (type !== "golf") return type;
  if (isGolfAllowed(originIcao, destIcao, month)) return "golf";
  const dest = destIcao.trim().toUpperCase();
  if ((SUN_DESTS as readonly string[]).includes(dest)) return "sun";
  return "vip";
}

export function jetRejectsCharterStage(
  spec: Pick<AircraftSpec, "icaoType" | "cruiseKt">,
  originIcao: string,
  destIcao: string,
  distanceNm: number | null,
  ferry = false,
): boolean {
  if (ferry || !isJetSpec(spec)) return false;
  if (isKnownShortRegionalHop(originIcao, destIcao)) return true;
  if (distanceNm == null) return false;
  return distanceNm <= JET_MIN_STAGE_NM;
}

function withNonCharterPurposes(row: Omit<PurposeWeights, "mx_payload" | "ferry">): PurposeWeights {
  return { ...row, mx_payload: 0, ferry: 0 };
}

export function purposeWeights(month: number): PurposeWeights {
  switch (month) {
    case 1:
      return withNonCharterPurposes({ golf: 0, sun: 26, corporate: 14, vip: 8, medical: 8, family: 16, hunting: 0, fishing: 0, ski: 14, tourism: 0, connection: 6 });
    case 2:
      return withNonCharterPurposes({ golf: 0, sun: 24, corporate: 14, vip: 8, medical: 8, family: 16, hunting: 0, fishing: 0, ski: 16, tourism: 0, connection: 6 });
    case 3:
      return withNonCharterPurposes({ golf: 0, sun: 16, corporate: 18, vip: 10, medical: 8, family: 14, hunting: 0, fishing: 8, ski: 10, tourism: 2, connection: 6 });
    case 4:
      return withNonCharterPurposes({ golf: 0, sun: 8, corporate: 20, vip: 12, medical: 8, family: 14, hunting: 0, fishing: 14, ski: 4, tourism: 8, connection: 6 });
    case 5:
      return withNonCharterPurposes({ golf: 26, sun: 2, corporate: 14, vip: 10, medical: 8, family: 10, hunting: 0, fishing: 10, ski: 0, tourism: 16, connection: 6 });
    case 6:
      return withNonCharterPurposes({ golf: 30, sun: 0, corporate: 12, vip: 10, medical: 8, family: 8, hunting: 0, fishing: 10, ski: 0, tourism: 18, connection: 6 });
    case 7:
      return withNonCharterPurposes({ golf: 32, sun: 0, corporate: 10, vip: 10, medical: 8, family: 8, hunting: 0, fishing: 8, ski: 0, tourism: 20, connection: 6 });
    case 8:
      return withNonCharterPurposes({ golf: 30, sun: 0, corporate: 10, vip: 10, medical: 8, family: 8, hunting: 0, fishing: 8, ski: 0, tourism: 22, connection: 6 });
    case 9:
      return withNonCharterPurposes({ golf: 26, sun: 2, corporate: 14, vip: 10, medical: 8, family: 8, hunting: 8, fishing: 6, ski: 0, tourism: 14, connection: 6 });
    case 10:
      return withNonCharterPurposes({ golf: 16, sun: 6, corporate: 16, vip: 10, medical: 8, family: 8, hunting: 14, fishing: 4, ski: 2, tourism: 12, connection: 6 });
    case 11:
      return withNonCharterPurposes({ golf: 0, sun: 18, corporate: 14, vip: 8, medical: 8, family: 12, hunting: 18, fishing: 0, ski: 8, tourism: 0, connection: 6 });
    default:
      return withNonCharterPurposes({ golf: 0, sun: 24, corporate: 12, vip: 8, medical: 8, family: 18, hunting: 4, fishing: 0, ski: 12, tourism: 0, connection: 6 });
  }
}

export function pickSeasonalPurpose(
  month: number,
  rng: () => number = Math.random,
  available: SeasonalPurposeId[] = (Object.keys(PURPOSE_TO_FLIGHT_TYPE) as SeasonalPurposeId[]).filter(
    (id) => id !== "mx_payload" && id !== "ferry",
  ),
): SeasonalPurposeId {
  const w = purposeWeights(month);
  const rows = available.map((item) => ({ item, weight: Math.max(0, w[item] ?? 0) }));
  const total = rows.reduce((s, r) => s + r.weight, 0);
  if (total <= 0) return available[0] ?? "corporate";
  let roll = rng() * total;
  for (const row of rows) {
    roll -= row.weight;
    if (roll <= 0) return row.item;
  }
  return rows[rows.length - 1]?.item ?? "corporate";
}

export function destsForPurpose(purpose: SeasonalPurposeId): readonly string[] {
  switch (purpose) {
    case "golf":
      return GOLF_MARKETS;
    case "sun":
      return SUN_DESTS;
    case "corporate":
      return CORPORATE_DESTS;
    case "vip":
      return VIP_DESTS;
    case "medical":
      return MEDICAL_DESTS;
    case "family":
      return FAMILY_DESTS;
    case "hunting":
      return HUNTING_DESTS;
    case "fishing":
      return FISHING_DESTS;
    case "ski":
      return SKI_DESTS;
    case "tourism":
      return TOURISM_DESTS;
    case "connection":
      return CONNECTION_DESTS;
    case "mx_payload":
      return [];
    case "ferry":
      return [];
    default:
      return [];
  }
}

export function preferOriginsForPurpose(
  purpose: SeasonalPurposeId,
  month: number,
  originHint?: string,
  homeIcaos: string[] = [],
): string[] {
  const hint = originHint?.trim().toUpperCase();
  const homes = homeIcaos.map((icao) => icao.trim().toUpperCase()).filter(Boolean);
  if (homes.length || hint) {
    const base = homes.length ? [...homes] : hint ? [hint] : [];
    if (!hint) return base;
    return [hint, ...base.filter((icao) => icao !== hint)];
  }
  void purpose;
  void month;
  return [];
}

export function preferDestsForPurpose(
  purpose: SeasonalPurposeId,
  month: number,
  bases: readonly string[] = [],
): string[] {
  const live = bases.map((icao) => icao.trim().toUpperCase()).filter((icao) => icao.length >= 3);
  if (purpose === "golf" && isGolfSeasonMonth(month)) {
    return live.filter((icao) => isAtlanticGolfRegion(icao));
  }
  const dests = [...destsForPurpose(purpose)];
  if (!live.length) return dests.filter((icao) => icao !== "CYPD");
  return dests;
}

export function purposeCaption(purpose: SeasonalPurposeId): string {
  switch (purpose) {
    case "golf":
      return "Golf inbound — Cabot / Cape Breton";
    case "sun":
      return "Vacation / southbound escape";
    case "corporate":
      return "Corporate / business";
    case "vip":
      return "VIP / leisure";
    case "medical":
      return "Air ambulance / medical";
    case "family":
      return "Visiting family";
    case "hunting":
      return "Hunting party";
    case "fishing":
      return "Fishing / lodge";
    case "ski":
      return "Ski / winter sport";
    case "tourism":
      return "Summer tourism — Cape Breton / Atlantic";
    case "connection":
      return "Missed airline / connecting flight";
    case "mx_payload":
      return "Positioning charter (mx city)";
    case "ferry":
      return "Company ferry / positioning";
    default:
      return "Charter";
  }
}

export function purposeReason(
  purpose: SeasonalPurposeId,
  client: string,
  firm: string,
  originName: string,
  destName: string,
  paxCount: number,
): { purpose: string; reason: string; scenario: string } {
  const title = purposeCaption(purpose);
  switch (purpose) {
    case "golf":
      return {
        purpose: title,
        reason: `${client} (${firm}) inbound for Cabot Links / Cabot Cliffs.`,
        scenario: `${client} + party of ${paxCount} ${originName}–${destName}. Clubs in the aft bay.`,
      };
    case "sun":
      return {
        purpose: title,
        reason: `${client} party leaving for sun / south.`,
        scenario: `${client} party of ${paxCount} ${originName} to ${destName}. Soft bags.`,
      };
    case "family":
      return {
        purpose: title,
        reason: `${client} visiting family — ${originName} to ${destName}.`,
        scenario: `${client} + ${Math.max(0, paxCount - 1)} flying ${originName}–${destName} to see family. Soft bags.`,
      };
    case "hunting":
      return {
        purpose: title,
        reason: `${client} hunting party — rifles / gear declared.`,
        scenario: `${client} (${firm}) party of ${paxCount} ${originName} to ${destName}. Hard cases in the aft bay.`,
      };
    case "fishing":
      return {
        purpose: title,
        reason: `${client} lodge / fishing lift.`,
        scenario: `${client} party of ${paxCount} ${originName}–${destName}. Rod tubes, waders, no press.`,
      };
    case "ski":
      return {
        purpose: title,
        reason: `${client} ski week — ${destName}.`,
        scenario: `${client} + ${Math.max(0, paxCount - 1)} ${originName} to ${destName}. Ski bags.`,
      };
    case "tourism":
      return {
        purpose: title,
        reason: `${client} summer tourism — Cabot Trail / Cape Breton.`,
        scenario: `${client} party of ${paxCount} ${originName}–${destName}. Soft bags, trail week.`,
      };
    case "connection":
      return {
        purpose: title,
        reason: `${client} missed the airline and needs a charter into ${destName} to catch the connecting flight.`,
        scenario: `${client} party of ${paxCount} ${originName}–${destName}. Missed commercial / connecting bank — hub is the story.`,
      };
    case "medical":
      return {
        purpose: title,
        reason: `Medevac desk for ${client}.`,
        scenario: `Medevac: ${client} + crew ${originName} to ${destName}.`,
      };
    case "mx_payload":
      return {
        purpose: title,
        reason: `${firm} — ${client} staff lift on a cheaper-than-usual mx positioning charter.`,
        scenario: `${firm} — ${client} and ${Math.max(0, paxCount - 1)} staff ${originName} to ${destName}. Paying charter on the shop positioning.`,
      };
    case "ferry":
      return {
        purpose: title,
        reason: `Empty company ferry ${originName}–${destName}. No charter revenue.`,
        scenario: `Company ferry ${originName} to ${destName}. No paying pax or cargo — not a revenue charter.`,
      };
    case "vip":
      return {
        purpose: title,
        reason: `${client} requests a quiet lift.`,
        scenario: `${client} (${firm}) ${originName} to ${destName}. Catering on file.`,
      };
    default:
      return {
        purpose: title,
        reason: `${firm} — ${client} staff lift.`,
        scenario: `${firm} — ${client} and ${Math.max(0, paxCount - 1)} staff. ${originName} to ${destName}.`,
      };
  }
}

export function purposeFromFlightType(
  type: FlightType,
  month: number,
  destIcao?: string,
  originIcao?: string,
): SeasonalPurposeId {
  if (type === "golf") return isGolfAllowed(originIcao ?? "", destIcao ?? "", month) ? "golf" : "family";
  if (type === "sun") return "sun";
  if (type === "medical") return "medical";
  if (type === "ferry") return "ferry";
  if (type === "corporate") return "corporate";
  const dest = destIcao?.toUpperCase() ?? "";
  if ((SKI_DESTS as readonly string[]).includes(dest) && (month <= 3 || month === 12 || month === 11)) return "ski";
  if ((HUNTING_DESTS as readonly string[]).includes(dest) && (month >= 9 && month <= 12)) return "hunting";
  return "vip";
}

export function seasonHintForPrompt(month: number, year: number, bases: readonly string[] = []): string {
  const id: SeasonId = seasonId(month);
  const live = bases.map((icao) => icao.trim().toUpperCase()).filter((icao) => icao.length >= 3);
  const where = live.join(", ");
  if (!live.length) {
    return "No live company base on this prompt. Do not assume Port Hawkesbury, CYPD, or a Celtic seasonal pad. No golf inbound to Cape Breton unless a current base is there.";
  }
  if (basesLookAtlantic(live)) {
    if (id === "golf") {
      return `Golf and summer tourism are in season around ${where}. Cabot / Cape Breton golf only when a current base is in that region. Hunting starts late. No ski.`;
    }
    if (id === "winter") {
      return `No golf inbound to Cape Breton / nearby (CYPD, CYQY, CYYG, CYHZ) in winter. From ${where}: sun, family, business, medical, and ski where the allow-list supports it.`;
    }
    if (id === "shoulder-spring") {
      return `Shoulder from ${where}: fishing, family, business. Cabot golf is not open yet — do not invent golf inbound to CYPD unless it is still a current base.`;
    }
    return `Shoulder from ${where}: hunting and southbound sun. No golf inbound to Cape Breton.`;
  }
  if (id === "golf") {
    return `Leisure and business are in season from ${where}. Golf belongs at fields near those bases. Do not invent Cabot / Port Hawkesbury inbound.`;
  }
  if (id === "winter") {
    return `Winter demand from ${where}: business, family, medical, and sun south of those bases when the field is on the allow-list. No golf inbound to Cape Breton unless a current base is there.`;
  }
  return `Shoulder season from ${where}. Business and family first. Destinations sit in range of the current bases. Canada is allowed when the trip is realistic; it is not the default when the bases are elsewhere.`;
}

/**
 * Inspection-due (or soon) tails may carry a paying charter to the MRO city.
 * Ground-squawk / grounding MEL / already in mx or on ferry stay empty or stay put.
 */
export function canCarryMxPayload(
  spec: AircraftSpec,
  squawks: Squawk[] = [],
  melItems: MelItem[] = [],
): boolean {
  if (spec.status === "in_mx" || spec.status === "ferry") return false;
  if (hasOpenGroundSquawk(spec.id, squawks) || hasGroundingMel(spec.id, melItems)) return false;
  if (anyInspectionOverdue(spec)) return true;
  return hoursToInspection(spec) <= MX_PAYLOAD_SOON_HOURS;
}

export type MxPayloadOpportunity = {
  spec: AircraftSpec;
  origin: SceneryAirport;
  dest: SceneryAirport;
  mro: CompanyMxBase;
};

export function findMxPayloadOpportunity(input: {
  fleet: AircraftSpec[];
  scenery: SceneryAirport[];
  squawks?: Squawk[];
  melItems?: MelItem[];
  homeIcao?: string;
  /** Company MX bases. Empty means no shop hop — never a built-in Canada list. */
  mxBases?: readonly CompanyMxBase[];
  rng?: () => number;
}): MxPayloadOpportunity | null {
  const bases = input.mxBases ?? [];
  if (!bases.length) return null;
  const squawks = input.squawks ?? [];
  const melItems = input.melItems ?? [];
  const home = (input.homeIcao ?? "").toUpperCase();
  const rng = input.rng ?? Math.random;
  const tails = input.fleet.filter((spec) => canCarryMxPayload(spec, squawks, melItems));
  const shuffled = [...tails].sort(() => rng() - 0.5);

  for (const spec of shuffled) {
    const originIcao = (spec.lastAirportIcao || home).toUpperCase();
    const origin = hydrateSceneryAirport(originIcao, input.scenery);
    if (!origin) continue;
    const dests = bases
      .map((mro) => {
        const dest = hydrateSceneryAirport(mro.icao, input.scenery);
        if (!dest || dest.icao.toUpperCase() === origin.icao.toUpperCase()) return null;
        const oc = resolveCoords(origin.icao, [origin, ...input.scenery]);
        const dc = resolveCoords(dest.icao, [dest, ...input.scenery]);
        const nm = oc && dc ? haversineNm(oc.lat, oc.lon, dc.lat, dc.lon) : null;
        if (jetRejectsCharterStage(spec, origin.icao, dest.icao, nm)) return null;
        if (nm != null && nm > spec.rangeNm * CHARTER_RANGE_FACTOR) return null;
        return { spec, origin, dest, mro, nm: nm ?? 0 };
      })
      .filter((row): row is NonNullable<typeof row> => Boolean(row))
      .sort((a, b) => b.nm - a.nm);

    const hit = dests[0];
    if (hit) return { spec: hit.spec, origin: hit.origin, dest: hit.dest, mro: hit.mro };
  }
  return null;
}

export function destIsMroForType(
  destIcao: string,
  _icaoType: string,
  mxBases?: readonly CompanyMxBase[],
): boolean {
  const dest = destIcao.trim().toUpperCase();
  if (!dest) return false;
  return (mxBases ?? []).some((row) => row.icao.toUpperCase() === dest);
}

/** Clamp souls to what a small CYPD charter tail can actually seat. */
export function clampCharterPax(paxCount: number, spec: Pick<AircraftSpec, "maxPax">): number {
  return Math.max(0, Math.min(spec.maxPax, Math.round(paxCount)));
}

export const GROK_INBOX_SYSTEM =
  "You write incoming charter-desk emails for the operator named in the user prompt at that company's current bases (primary, extras, and per-tail bases). Use the desk certificate from the user prompt. Do not label RFQs as CAR 703 Air Taxi unless the desk certificate is 703. Mix formal broker RFQs and casual client notes. Output valid JSON only — the object shape is {\"inquiries\":[...]}. When the Destinations allow-list has two or more legal fields and an assignable airframe exists, inquiries MUST be non-empty. An empty inquiries array is a failed answer. Follow the realism rules in the user prompt. Never invent ICAOs, cash, Navigraph data, or a ledger balance. Normal private-charter destinations prefer executive / GA / reliever fields near the current bases; a commercial hub is the backup when no suitable executive field is usable, and the primary only for missed-connection stories. Diversify dest geography inside that base region — do not sticky-loop MYNN/KBCT/KSWF or a former Port Hawkesbury set. A Dispatcher-written company charter brief in the user prompt may tighten or relax that dest mix — follow it when present. Honor mint-memory cool-downs and crew distance prefs as soft hints only. When Duty Standby is armed in the user prompt, bias a majority of STDs into that standby window (soon enough to accept and go; readiness ~30–60 minutes is the ops intent, not a hard airborne-in-30-min rule) and keep a smaller share 1–2 days ahead for planning. Never default originIcao to CYPD or any other leftover fiction pad unless that ICAO is a current company base or the suggested tail's assigned base. Most destinations are realistic charter demand from those current bases. Canada and the US are both legal when the trip fits.";

const GROK_STAGE_RULE =
  "Aircraft vs stage length: do NOT put a jet (H25B / Hawker 800XP) on a hop under about 120 NM, or on a known Atlantic short (CYPD–CYHZ, CYPD–CYQM, CYPD–CYQY, CYPD–CYYG, CYHZ–CYQM) when that city-pair is the trip. Those belong on the turboprop (PC-12) or must be skipped for the jet. A home-region hop such as KTEB–KBOS or KTEB–KBED is a normal charter, not an Atlantic short. Prefer realistic jet sectors from the current company bases: the executive or reliever field in that region first, then a hub in the same region if no exec field is legal. Canada, the US, Caribbean, and Mexico only when those ICAOs are on the allow-list and the stage fits — not a sticky MYNN/KBCT/CYPD loop. Do not default a famous hub in another region when a field nearer the current bases is legal.";

const GROK_SEASON_RULE =
  "Season + purpose follow the current bases, not a frozen Cape Breton desk. No golf inbound to Cape Breton or nearby (CYPD, CYQY, CYYG, CYHZ) outside May–Oct, and never as the default dest when no current base is in that region. Use vacation/sun, business, medical, visiting family, hunting/fishing, and ski when they match the base region and the allow-list.";

const GROK_REALISM_TAIL = [
  "Pax counts must fit the suggested tail (Hawker typical 8 pax + 2 crew; PC-12 typically ≤ 8). Do not invent a 16-seat cabin.",
  "When a company tail must position to an MRO / mx facility, prefer a paying charter on that positioning leg when the city-pair is a realistic charter sector — not only an empty ferry. Do not carry pax on a ground-squawk or grounding-MEL tail. Empty ferries (no paying pax/cargo) are never revenue — use flightType=ferry and do not set mxPositioning. Set mxPositioning=true only when a paying (pax or cargo) cheaper-than-usual charter is offered on that mx positioning sector.",
  "One-way outbound from a current base usually implies an empty return ferry unless a cheaper reverse / backhaul fill rides. Empty return cost is baked into the desk quote (not a second pax sector). A taken backhaul is paying charter (discounted vs outbound). Occasional multi-stop: drop one party / carry two. Occasional tech-stop when the city-pair exceeds practical range — viaIcao only from the dest allow-list; that hop is not pax revenue.",
  "Regional regs: use the desk certificate from the user prompt. Never label an RFQ as CAR 703 Air Taxi unless the desk certificate is 703.",
  "Do not invent ICAOs, dollar amounts, Navigraph charts, or weather products. Destinations must be on the allow-list.",
  "When Duty Standby is armed, most departureIso values belong inside the armed standby window so the crew can accept and go. A smaller share may stay 1–2 days ahead. When standby is cold, keep the usual next-14-days STD spread.",
  "Runway length is PIC judgment. Under 5000 ft: caution only. Do not rematch or refuse a city-pair solely because the strip is short.",
  "Requester names, companies, and email domains match the city pair (origin or destination). Home base only flavors the name. Never use the operator's own company as the client. No joke names. Mix private individuals with local and out-of-region companies.",
  "Seats, range, and type come only from the company fleet lines in this prompt. Do not invent a tail that is not on that list.",
];

export function grokRealismRulesFor(bases: readonly string[] = []): string {
  return [GROK_STAGE_RULE, formatGrokInboxAirportBrief(bases), GROK_SEASON_RULE, ...GROK_REALISM_TAIL].join("\n");
}

export const GROK_REALISM_RULES = grokRealismRulesFor();
